import { emailIsValid, phoneIsValid, zipCodeIsValid } from "../Utils/Helpers";
import { AreaOfInterest } from "../V2/Playbook/Interests/Types/AreaOfInterest";
import IContactInfo from "../V2/Playbook/Search/Types/IContactInfo";
import V2Customer from "../V2/Playbook/Search/Types/V2Customer";
import { Center, EnhancedCenter } from "./Center";
import { Opportunity } from "./Opportunity";

export enum Gender {
  NotSpecified = 0,
  Male = 1,
  Female = 2
}

/** A temporary map from an improvement areas key to its areas of interest display name. */
export const ImprovementAreasMap: Record<keyof ImprovementAreas, string> = {
  abdomen: "Abdomen",
  arms: "Arms",
  back: "Back",
  bananaRoll: "Banana Roll",
  braRolls: "Bra Rolls",
  breastAug: "Breast Augmentation",
  chinFat: "Chin Fat",
  faceWrinkleReduction: "Face Wrinkle Reduction",
  legs: "Legs",
  maleChest: "Male Chest Lipo",
  thighs: "Thighs",
  waist: "Waist"
};

export class ImprovementAreas {
  abdomen: boolean;
  arms: boolean;
  back: boolean;
  bananaRoll: boolean;
  braRolls: boolean;
  breastAug: boolean;
  chinFat: boolean;
  faceWrinkleReduction: boolean;
  legs: boolean;
  maleChest: boolean;
  thighs: boolean;
  waist: boolean;

  constructor() {
    this.abdomen = false;
    this.arms = false;
    this.back = false;
    this.bananaRoll = false;
    this.braRolls = false;
    this.breastAug = false;
    this.chinFat = false;
    this.faceWrinkleReduction = false;
    this.legs = false;
    this.maleChest = false;
    this.thighs = false;
    this.waist = false;
  }

  /** Map from the Area Of Interest model into an ImprovementAreas model. */
  static fromAreasOfInterest = (value: Record<string, boolean>, options: AreaOfInterest[]): ImprovementAreas => {
    const improvementAreas = new ImprovementAreas();
    Object.entries(value)
      .filter(([, value]) => value)
      .forEach(([key]) => {
        const areaOfInterestName = options.find(o => o.id == key)?.name;
        if (!areaOfInterestName) {
          console.warn("Failed to look up an improvement area by it area of interest name.");
          return;
        }
        const improvementAreasKey = Object.entries(ImprovementAreasMap).find(
          ([, value]) => value == areaOfInterestName
        )?.[0];
        if (!improvementAreasKey) {
          console.warn("Failed to look up an improvement area by it area of interest name.");
          return;
        }
        improvementAreas[improvementAreasKey as keyof ImprovementAreas] = true;
      });
    return improvementAreas;
  };

  /** Map from an ImprovementAreas model into an Areas Of Interest model. */
  static toAreasOfInterest = (value: ImprovementAreas, options: AreaOfInterest[]): Record<string, boolean> => {
    const areasOfInterest: Record<string, boolean> = {};
    Object.entries(value)
      .filter(([, value]) => value)
      .forEach(([key]) => {
        const areaOfInterestName = ImprovementAreasMap[key as keyof ImprovementAreas];
        const areaOfInterest = options.find(o => o.name == areaOfInterestName);
        if (!areaOfInterest) {
          console.warn("Failed to look up an area of interest by its improvement area name.");
          return;
        }
        areasOfInterest[areaOfInterest.id] = true;
      });
    return areasOfInterest;
  };
}

export class Customer implements IContactInfo {
  id!: string | null;
  externalId!: string | null;
  code!: string | null;
  firstName!: string | null;
  lastName!: string | null;
  phoneNumber!: string | null;
  homePhoneNumber!: string | null;
  workPhoneNumber!: string | null;
  email!: string | null;
  zipCode!: string | null;
  height!: { feet: number; inches: number } | null;
  weight!: number | null;
  bmi!: number | null;
  dateOfBirth!: string | null;
  gender!: Gender;
  improvementAreas!: ImprovementAreas;
  centerId!: string | null;
  center?: Center;
  distanceToCenter!: number | null;
  opportunity!: Opportunity | null;
  shallow!: boolean;
}

export class EnhancedCustomer extends Customer {
  center?: EnhancedCenter;

  constructor(customer?: Partial<Customer>) {
    super();

    this.id = customer?.id || null;
    this.externalId = customer?.externalId || null;
    this.code = customer?.code || null;
    this.firstName = customer?.firstName || null;
    this.lastName = customer?.lastName || null;
    this.gender = customer?.gender || Gender.NotSpecified;
    this.email = customer?.email || null;
    this.phoneNumber = customer?.phoneNumber || null;
    this.homePhoneNumber = customer?.homePhoneNumber || null;
    this.workPhoneNumber = customer?.workPhoneNumber || null;
    this.zipCode = customer?.zipCode || null;
    this.improvementAreas = customer?.improvementAreas || new ImprovementAreas();
    this.distanceToCenter = customer?.distanceToCenter || null;
    if (!customer?.centerId != !customer?.center)
      throw "Must construct a customer with both centerId and center props or neither prop.";
    this.centerId = customer?.centerId || null;
    this.center = customer?.center && new EnhancedCenter(customer.center);
    this.height = customer?.height || null;
    this.weight = customer?.weight || null;
    this.bmi = customer?.bmi || null;
    this.dateOfBirth = customer?.dateOfBirth || null;
    this.opportunity = customer?.opportunity || null;
    this.shallow = customer?.shallow || false;
  }

  /** Return flag indicating if there was a client-side change in the customer (not a back end property). */
  static isChange(customer1: EnhancedCustomer, customer2: EnhancedCustomer): boolean {
    // compare everything except for identifiers
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id: id1, externalId: exid1, code: code1, shallow: shallow1, ...rest1 } = customer1;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id: id2, externalId: exid2, code: code2, shallow: shallow2, ...rest2 } = customer2;
    return JSON.stringify(rest1) !== JSON.stringify(rest2);
  }

  /** Return a flag indicating if the customer is in a valid state to be saved to the CRM.
   * @description The param must have a center, first/last name, at minimum email or phone number, and an assigned
   * opportunity or campaign.
   */
  static isValid(customer: EnhancedCustomer): boolean {
    return Boolean(
      customer.centerId &&
        customer.firstName &&
        customer.lastName &&
        (phoneIsValid(customer.phoneNumber) || emailIsValid(customer.email)) &&
        (customer.opportunity?.id || customer.opportunity?.campaign?.id)
    );
  }

  /** Get a list of the primary properties of the customer which are invalid and should prevent session disposition. */
  static getInvalidPrimaryInfoItems(customer: EnhancedCustomer): string[] {
    const invalidPrimaryInfo: string[] = [];
    if (!customer.firstName || !customer.lastName) invalidPrimaryInfo.push("Full Name");
    if (!customer.centerId) invalidPrimaryInfo.push("Center");
    if (customer.zipCode && !zipCodeIsValid(customer.zipCode)) invalidPrimaryInfo.push("Zip Code");
    if (!customer.phoneNumber && !customer.email) invalidPrimaryInfo.push("Phone Number or Email Address");

    return invalidPrimaryInfo;
  }

  /** Transform the enhanced customer into its base representation. */
  static toBase(customer: EnhancedCustomer): Customer {
    return { ...customer, center: customer.center && EnhancedCenter.toBase(customer.center) };
  }

  /** Transform the v2 Customer format into this format. */
  static fromV2Customer(
    { areasOfInterest, ...rest }: V2Customer,
    areasOfInterestOptions: AreaOfInterest[],
    centers: EnhancedCenter[]
  ): EnhancedCustomer {
    return new EnhancedCustomer({
      ...rest,
      center: centers.find(c => c.id == rest.centerId),
      improvementAreas: ImprovementAreas.fromAreasOfInterest(areasOfInterest, areasOfInterestOptions)
    });
  }
}

/** An internal type to contain the raw and unvalidated states shared by ribbon inputs and stepper inputs. */
export class UnverifiedCustomer {
  dateOfBirth: string;
  email: string;
  firstName: string;
  feet: string;
  homePhoneNumber: string;
  inches: string;
  lastName: string;
  phoneNumber: string;
  weight: string;
  workPhoneNumber: string;
  zipCode: string;

  constructor(customer?: EnhancedCustomer) {
    this.dateOfBirth = customer?.dateOfBirth ?? "";
    this.email = customer?.email ?? "";
    this.firstName = customer?.firstName ?? "";
    this.feet = `${customer?.height?.feet ?? ""}`;
    this.homePhoneNumber = customer?.homePhoneNumber ?? "";
    this.inches = `${customer?.height?.inches ?? ""}`;
    this.lastName = customer?.lastName ?? "";
    this.phoneNumber = customer?.phoneNumber ?? "";
    this.weight = `${customer?.weight ?? ""}`;
    this.workPhoneNumber = customer?.workPhoneNumber ?? "";
    this.zipCode = customer?.zipCode ?? "";
  }
}
